import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '@/context/AppContext';
import { SyncPill } from './SyncPill';
import { NotificationBell } from './NotificationBell';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { Home as HomeIcon, ChevronLeft, Search, LogOut, Settings as SettingsIcon, ShieldCheck } from 'lucide-react';
import api from '@/lib/api';
import { money, STATUS_STYLES } from '@/lib/helpers';

const ROLE_LABEL = { OWNER: 'Owner', MANAGER: 'Manager', STAFF: 'Staff' };

// Desktop-only global garment search with live suggestions -> garment detail
const DesktopSearch = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setResults([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/garments', { params: { q: q.trim(), status: '', limit: 8 } });
        setResults(r.data.data); setOpen(true);
      } catch (e) { setResults([]); }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const go = (barcode) => {
    setQ(''); setResults([]); setOpen(false);
    navigate(`/garment/${String(barcode).trim().toUpperCase()}`);
  };

  return (
    <div className="relative w-full" ref={boxRef}>
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input data-testid="topbar-desktop-search" value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => { if (results.length) setOpen(true); }}
        onKeyDown={(e) => { if (e.key === 'Enter' && q.trim()) go(q); }}
        placeholder="Search barcode or product…" className="h-10 rounded-full pl-9" autoComplete="off" />
      {open && (
        <div className="absolute z-50 mt-1 max-h-80 w-full overflow-y-auto rounded-xl border border-border bg-popover shadow-lg"
          data-testid="topbar-search-suggestions">
          {results.length === 0 && <p className="px-3 py-3 text-sm text-muted-foreground">No matching garments.</p>}
          {results.map((g) => (
            <button key={g.id} type="button" data-testid={`topbar-search-option-${g.barcode}`} onClick={() => go(g.barcode)}
              className="flex w-full items-center justify-between gap-2 border-b border-border px-3 py-2.5 text-left last:border-0 hover:bg-accent/60">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{g.product || '(no name)'}</p>
                <p className="font-mono text-xs text-muted-foreground">{g.barcode} · {g.size || '-'} · {g.colour || '-'}</p>
              </div>
              <div className="flex flex-col items-end gap-0.5">
                <span className="font-mono text-sm font-semibold">{money(g.price)}</span>
                <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-medium ${STATUS_STYLES[g.status] || 'bg-slate-100'}`}>{g.status}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ProfileMenu = () => {
  const { user, logout } = useApp();
  const navigate = useNavigate();
  const initial = (user?.name || user?.username || '?')[0]?.toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="topbar-profile-trigger"
        className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring">
        <Avatar className="h-8 w-8">
          <AvatarFallback className="bg-primary text-xs font-semibold text-primary-foreground">{initial}</AvatarFallback>
        </Avatar>
        <span className="hidden max-w-[120px] truncate text-sm font-medium xl:inline">{user?.name || user?.username}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <p className="truncate text-sm font-semibold">{user?.name || user?.username}</p>
          <p className="text-xs font-normal text-muted-foreground">{ROLE_LABEL[user?.role] || user?.role}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {(user?.role === 'OWNER' || user?.owner_access) && (
          <DropdownMenuItem data-testid="topbar-profile-admin" onClick={() => navigate('/admin')}>
            <ShieldCheck className="mr-2 h-4 w-4" /> Admin / Users
          </DropdownMenuItem>
        )}
        {(user?.role === 'OWNER' || user?.owner_access) && (
          <DropdownMenuItem data-testid="topbar-profile-settings" onClick={() => navigate('/settings')}>
            <SettingsIcon className="mr-2 h-4 w-4" /> Settings
          </DropdownMenuItem>
        )}
        <DropdownMenuItem data-testid="topbar-profile-logout" onClick={logout} className="text-destructive focus:text-destructive">
          <LogOut className="mr-2 h-4 w-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const TopBar = ({ title, showStore = true, back = false, onBack = null, right = null, showHome = true }) => {
  const { stores, currentStore, selectStore, user, settings } = useApp();
  const navigate = useNavigate();
  const canSwitch = user && (user.role === 'OWNER' || user.role === 'MANAGER');
  const branding = settings?.branding || {};
  const showBack = back || !!onBack;
  const handleBack = () => {
    if (onBack) { onBack(); return; }
    navigate(-1);
  };
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-header/90 text-header-foreground backdrop-blur"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-1.5 px-3 lg:h-16 lg:px-6">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          {/* Mobile: always-available Home shortcut back to the main dashboard. */}
          {showHome && (
            <button data-testid="topbar-home" onClick={() => navigate('/')} aria-label="Home"
              className="-ml-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full hover:bg-accent lg:hidden">
              <HomeIcon className="h-5 w-5" />
            </button>
          )}
          {showBack && (
            <button data-testid="topbar-back" onClick={handleBack}
              className="-ml-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-accent lg:h-9 lg:w-9">
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          {branding.logo && (
            <img src={branding.logo} alt="Logo" data-testid="topbar-logo"
              className="h-7 w-auto max-w-[64px] shrink-0 object-contain lg:h-9 lg:max-w-[120px]" />
          )}
          <div className="min-w-0">
            <h1 className="font-display truncate text-base font-semibold tracking-tight lg:text-lg">{title}</h1>
          </div>
        </div>

        {/* Desktop global search */}
        <div className="hidden flex-1 justify-center px-6 lg:flex">
          <div className="w-full max-w-md"><DesktopSearch /></div>
        </div>

        <div className="flex shrink-0 items-center gap-1 lg:gap-2">
          {right}
          {showStore && (
            canSwitch ? (
              <Select value={currentStore} onValueChange={selectStore}>
                <SelectTrigger data-testid="store-switcher" className="h-9 w-[92px] rounded-full px-2.5 text-xs lg:w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {stores.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-medium">
                {stores.find((s) => s.id === currentStore)?.name || 'Store'}
              </span>
            )
          )}
          <NotificationBell />
          <SyncPill />
          <div className="hidden lg:block"><ProfileMenu /></div>
        </div>
      </div>
    </header>
  );
};
